import { type ComputedRef, type Ref, computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import type {
  FunnelStep,
  GetQuestionsKind,
  Option,
  Question,
  Step,
} from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { hashObjects } from '@backmarket/utils/object/hashObjects'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import translations from '../components/QuestionsForm/QuestionsForm.translations'

export type FunnelForm = Record<string, string | string[]>

export interface Info {
  text: Array<string> | null
  tag: 'ul' | 'p'
  subTag: 'li' | 'span'
}

export interface RenderOption extends Omit<Option, 'icon'> {
  inputId: string
  icon: string | undefined
  imagesSrc?: { src: string; srcMobile: string }
}

export interface RenderQuestion extends Omit<Question, 'options'> {
  value: string | string[] | false
  showInfo: boolean
  info: Info
  image: string | null
  options: Array<RenderOption>
  isSelect: boolean
  isRadio: boolean
  isCheckbox: boolean
  isConfirmation: boolean
  isPickerWithImage: boolean
  isSmileyImage: boolean
}

const QUESTION_TYPES = {
  SELECT: 'select',
  RADIO: 'radioline',
  CHECKBOX: 'multi_select',
  CONFIRMATION: 'confirmation',
} as const

const PICKER_WITH_IMAGE_KEYS = new Set(['state_screen', 'state_body'])

const DEFAULTS_LOOKUP: Record<string, string | false | string[]> = {
  [QUESTION_TYPES.RADIO]: false,
  [QUESTION_TYPES.SELECT]: '',
  [QUESTION_TYPES.CHECKBOX]: [],
  [QUESTION_TYPES.CONFIRMATION]: '1',
}

interface UseFunnelQuestionsOptions {
  kind: GetQuestionsKind
  onStepChange?: (step: Step, formPayload: FunnelForm) => void
  onReadyForOffer: (formPayload: FunnelForm) => void
  onUnprocessableEntity: () => void | Promise<void>
}

export type { Step, FunnelStep }

export function useFunnelQuestions({
  kind,
  onStepChange,
  onReadyForOffer,
  onUnprocessableEntity,
}: UseFunnelQuestionsOptions): {
  activeStep: ComputedRef<Step | null>
  activeQuestions: ComputedRef<RenderQuestion[]>
  funnel: Ref<FunnelStep[]>
  form: Ref<FunnelForm>
  isLoading: Ref<boolean>
  fetchError: Ref<Error | null>
  isStepComplete: ComputedRef<boolean>
  isLastQuestion: ComputedRef<boolean>
  hasSubmitButton: ComputedRef<boolean>
  buttonLabel: ComputedRef<string>
  fetchQuestionsWithPayload: (payload: FunnelForm) => Promise<boolean>
  handleAnswer: (key: string, value: string | string[] | false) => Promise<void>
  goToPreviousStep: () => Promise<void>
} {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()

  const funnel = ref<FunnelStep[]>([])
  const form = ref<FunnelForm>({})
  const isLoading = ref(false)
  const fetchError = ref<Error | null>(null)
  const history = ref<Record<string, { funnel: FunnelStep[] }>>({})

  const activeFunnel = computed(() =>
    funnel.value.find(({ step }) => step?.active),
  )

  const activeStep = computed<Step | null>(() => {
    return activeFunnel.value?.step ?? null
  })

  const isStepComplete = computed(() => activeStep.value?.completed || false)

  const activeStepQuestions = computed<Question[]>(() => {
    return activeFunnel.value?.questions || []
  })

  const isLastQuestion = computed(() =>
    activeStepQuestions.value.some((q) => q.lastQuestion === true),
  )

  const buttonLabel = computed(() =>
    isLastQuestion.value
      ? i18n(translations.seeOffer)
      : i18n(translations.continue),
  )

  // Implement a logic to display or not the submit button based on question type
  const hasSubmitButton = computed(() => false)

  const activeQuestions = computed<RenderQuestion[]>(() => {
    return activeStepQuestions.value.map(
      (question: Question): RenderQuestion => {
        const infoList = question.subTitleList
        const hasOptions = !isEmpty(question.options)
        const defaultValue = DEFAULTS_LOOKUP[question.type] ?? ''
        const preSelected = question.options.find((opt) => opt.selected)

        const resolvedValue =
          form.value[question.key] ??
          (preSelected ? preSelected.value : defaultValue)

        const categoryValue =
          typeof form.value.category === 'string' ? form.value.category : ''
        const isPickerWithImage =
          PICKER_WITH_IMAGE_KEYS.has(question.key) &&
          categoryValue === 'smartphone'

        return {
          ...question,
          value: resolvedValue,
          showInfo: !isEmpty(infoList),
          info: {
            text: infoList,
            tag: infoList && infoList.length > 1 ? 'ul' : 'p',
            subTag: infoList && infoList.length > 1 ? 'li' : 'span',
          },
          options: hasOptions
            ? question.options.map((option) => ({
                ...option,
                inputId: `${option.label}-${option.value}`,
                icon: option?.icon?.light,
                imagesSrc: isPickerWithImage
                  ? {
                      src: `/img/buyback/${categoryValue}/${question.key}/${option.value}.png`,
                      srcMobile: `/img/buyback/${categoryValue}/${question.key}/${option.value}-mobile.png`,
                    }
                  : undefined,
              }))
            : [],
          infoBlock: question.infoBlock,
          tag: question.tag,
          isSelect: question.type === QUESTION_TYPES.SELECT,
          isRadio: question.type === QUESTION_TYPES.RADIO,
          isCheckbox: question.type === QUESTION_TYPES.CHECKBOX,
          isConfirmation: question.type === QUESTION_TYPES.CONFIRMATION,
          isPickerWithImage,
          isSmileyImage:
            question.key === 'functional_confirmation' ||
            question.key === 'not_functional_confirmation',
        }
      },
    )
  })

  async function fetchQuestionsWithPayload(
    payload: FunnelForm,
  ): Promise<boolean> {
    const { nextStep: _nextStep, ...formData } = payload
    form.value = { ...formData }

    const cacheKey = hashObjects(payload)
    const cached = history.value[cacheKey]

    if (cached) {
      funnel.value = cached.funnel

      return true
    }

    isLoading.value = true
    fetchError.value = null

    try {
      const query = {
        ...payload,
        embedded: 'true',
      }

      const response = await $httpFetch(
        '/buyback-funnel/api/v1/funnel/:kind/questions',
        {
          pathParams: { kind },
          query,
        },
      )

      funnel.value = response?.funnel || []
      history.value[cacheKey] = { funnel: funnel.value }

      return true
    } catch (error) {
      const httpError = HttpApiError.fromAnyError(error)
      if (httpError.status === 422) {
        await onUnprocessableEntity()
      } else {
        fetchError.value = error as Error
      }

      return false
    } finally {
      isLoading.value = false
    }
  }

  async function handleAnswer(
    key: string,
    value: string | string[] | false,
  ): Promise<void> {
    const updatedForm =
      value !== false ? { ...form.value, [key]: value } : { ...form.value }

    if (activeStep.value) {
      onStepChange?.(activeStep.value, updatedForm)
    }

    const succeeded = await fetchQuestionsWithPayload({
      ...updatedForm,
      nextStep: 'true',
    })

    if (!succeeded) return

    if (isLastQuestion.value && isStepComplete.value) {
      onReadyForOffer(form.value)
    }
  }

  async function goToPreviousStep(): Promise<void> {
    const activeFunnelStep = funnel.value.find(({ step }) => step?.active)

    activeFunnelStep?.questions.forEach(({ key }) => {
      delete form.value[key]
    })

    await fetchQuestionsWithPayload({ ...form.value, nextStep: 'false' })

    funnel.value = funnel.value.map((funnelStep) => {
      if (!funnelStep.step?.active) return funnelStep

      return {
        ...funnelStep,
        questions: funnelStep.questions.map((q) => {
          if (q.type !== QUESTION_TYPES.SELECT) return q
          delete form.value[q.key]

          return {
            ...q,
            options: q.options.map((opt) => ({ ...opt, selected: false })),
          }
        }),
      }
    })
  }

  return {
    activeStep,
    activeQuestions,
    funnel,
    form,
    isLoading,
    fetchError,
    isStepComplete,
    isLastQuestion,
    hasSubmitButton,
    buttonLabel,
    fetchQuestionsWithPayload,
    handleAnswer,
    goToPreviousStep,
  }
}
